/* eslint-disable react-hooks/exhaustive-deps */
import {
  previewEmailAPI,
  sendEmailAPI,
  suggestEmailAPI,
  getEmailTypesAPI,
  fetchUpcomingTripsAPI,
} from "Api/emailAPI";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import PreviewEmailModal from "./preview-email/previewEmail";
import { consoleLog } from "helpers";

const exemptTemplates = ["WELCOME", "RESETPASSWORD", "PROFILEUPDATE"];

function EmailTrigger() {
  const [formData, setFormData] = useState({
    email: "",
    itineraryId: "",
    emailTypes: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenPreviewModal, setIsOpenPreviewModal] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
  const [emailTypes, setEmailTypes] = useState([]);
  const [trips, setTrips] = useState([]);

  useEffect(() => {
    const fetchEmailTypes = async () => {
      const token = localStorage.getItem("jwtToken");
      try {
        const response = await getEmailTypesAPI(token);
        setEmailTypes(response);
      } catch (error) {
        console.error("Error fetching email types", error);
      }
    };
    fetchEmailTypes();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEmailChange = async (e) => {
    const email = e.target.value;
    setFormData((prev) => ({ ...prev, email }));

    if (email) {
      setIsSuggestionsLoading(true);
      try {
        const token = localStorage.getItem("jwtToken");
        const response = await suggestEmailAPI(email, token);
        setSuggestions(response);
      } catch (error) {
        console.error("Error fetching email suggestions:", error);
        setSuggestions([]);
      } finally {
        setIsSuggestionsLoading(false);
      }
    } else {
      setSuggestions([]);
    }
  };

  const handleEmailSelect = async ({ email, userId }) => {
    setFormData((prev) => ({ ...prev, email }));
    setSuggestions([]);
    try {
      const token = localStorage.getItem("jwtToken");
      const upcoming = await fetchUpcomingTripsAPI(userId, token);
      if (upcoming && upcoming.length > 0) {
        setTrips(upcoming);
        setFormData((prev) => ({ ...prev, itineraryId: upcoming[0] }));
      } else {
        toast.error("No upcoming trips found for this user.");
      }
    } catch (error) {
      console.error("Error fetching upcoming trips:", error);
      toast.error("Failed to fetch upcoming trips.");
    }
  };

  const cleanPayload = (data) => {
    const payload = { ...data };
    if (exemptTemplates.includes(payload.emailType)) {
      delete payload.itineraryId;
    }
    return payload;
  };

  const handlePreviewEmail = useCallback(async () => {
    if (isLoading) return;
    if (!validateForm()) return;
    setIsLoading(true);
    const token = localStorage.getItem("jwtToken");
    const toPreview = cleanPayload({
      ...formData,
      isPreview: true,
    });
    try {
      consoleLog(["📤 Preview payload:", toPreview]);
      const response = await previewEmailAPI(toPreview, setIsLoading, token);
      consoleLog(["📝 Raw previewEmailAPI result:", response]);
      if (response?.html) {
        setIsOpenPreviewModal(true);
      } else {
        throw new Error("No HTML in preview response");
      }
    } catch (err) {
      console.error("Error previewing Email:", err);
      toast.error("Failed to preview email");
    } finally {
      setIsLoading(false);
    }
  }, [formData, isLoading]);

  const handleSendEmail = useCallback(async () => {
    if (isLoading) return;
    if (!validateForm()) return;
    setIsLoading(true);
    const token = localStorage.getItem("jwtToken");
    const toSend = cleanPayload(formData);
    try {
      const response = await sendEmailAPI(toSend, setIsLoading, token);
      if (response) {
        toast.success("Email sent successfully");
        setFormData({ email: "", itineraryId: "", emailTypes: "" });
        setTrips([]);
      } else {
        throw new Error("Send API returned falsy");
      }
    } catch (err) {
      console.error("Error sending Email:", err);
      toast.error("Failed to send email");
    } finally {
      setIsLoading(false);
    }
  }, [formData, isLoading]);

  const validateForm = () => {
    if (!formData.emailTypes) {
      toast.error("Please select an email template.");
      return false;
    }

    if (!formData.email) {
      toast.error("Please enter an email address.");
      return false;
    }

    if (
      !exemptTemplates.includes(formData.emailTypes) &&
      !formData.itineraryId
    ) {
      toast.error("Please select or enter an itinerary ID.");
      return false;
    }

    return true;
  };

  return (
    <div className="flex min-h-[80vh] items-center justify-center ">
      <div className="w-full max-w-2xl rounded-xl bg-white p-8 shadow-md dark:!bg-gray-10 dark:text-white">
        <h2 className="mb-6 text-center text-2xl font-semibold text-gray-800 dark:text-white">
          Send Email
        </h2>
        <form className="space-y-6">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-white">
              Email Template
            </label>
            <select
              name="emailTypes"
              value={formData.emailTypes}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
            >
              <option value="">Select an email template</option>
              {emailTypes.map((type, idx) => (
                <option key={idx} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700  dark:text-white">
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                name="email"
                autoComplete="off"
                value={formData.email}
                onChange={handleEmailChange}
                className="w-full rounded-md border border-gray-300 px-4 py-2 pr-10 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                placeholder="Enter email address"
              />
              {formData.email && (
                <button
                  type="button"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      email: "",
                      itineraryId: "",
                    }));
                    setSuggestions([]);
                    setTrips([]);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  &#10005;
                </button>
              )}
            </div>

            {suggestions.length > 0 && (
              <ul className="mt-2 max-h-40 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-md">
                {isSuggestionsLoading ? (
                  <li className="px-4 py-2 text-gray-500">Loading...</li>
                ) : (
                  suggestions.map((s, idx) => (
                    <li
                      key={idx}
                      className="cursor-pointer px-4 py-2 hover:bg-gray-100 dark:!bg-gray-10 dark:text-white"
                      onClick={() => handleEmailSelect(s)}
                    >
                      {s.email}
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>

          {!exemptTemplates.includes(formData.emailTypes) && (
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-white">
                Itinerary ID
              </label>
              {trips.length > 0 ? (
                <select
                  name="itineraryId"
                  value={formData.itineraryId}
                  onChange={handleChange}
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                >
                  {trips.map((trip, idx) => (
                    <option key={idx} value={trip}>
                      {trip}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  name="itineraryId"
                  value={formData.itineraryId}
                  onChange={handleChange}
                  disabled
                  className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                  placeholder="Enter itinerary ID"
                />
              )}
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={handlePreviewEmail}
              disabled={isLoading}
              className="flex-1 transform rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-all duration-200 ease-in-out hover:bg-gray-50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Preview
            </button>
            <button
              type="button"
              onClick={handleSendEmail}
              disabled={isLoading}
              className="flex-1 transform rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-all duration-200 ease-in-out hover:bg-blue-700 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 active:scale-95 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Send
            </button>
          </div>
        </form>
      </div>
      <PreviewEmailModal
        isOpen={isOpenPreviewModal}
        onClose={() => setIsOpenPreviewModal(false)}
        previewEmailFormData={formData}
      />
    </div>
  );
}

export default EmailTrigger;
